import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild} from "@angular/core";

import {
    BehaviorSubject,
    mergeMap,
    Observable,
    map,
    share,
    switchMap,
    tap,
    distinctUntilChanged
} from "rxjs";

import { PaginationOptionsModel } from "../../../shared-library/form-controls/pagination/pagination-options.model";
import { BrowserWindowService } from "../../../shared-library/services/environment/browser-window.service";
import { PaginationOptionsFactoryService } from "../services/pagination-options-factory.service";
import { SearchFilterMetadataModel } from "../models/search-filter-metadata.model";
import { BaseSearchResultsModel } from "../models/base-search-results.model";
import { DeliveryApiService } from "../../../shared-library/delivery-api/delivery-api.service";
import { DirectorySearchPageModel } from "../../../shared-library/delivery-api/models/directory-search-page.model";
import { LockService } from "../services/lock.service";
import { GoogleMapComponent } from "./components/google-map/google-map.component";
import { SearchParametersModel } from "./models/search-parameters.model";
import { DirectorySearchParametersFactoryService } from "./services/directory-search-parameters-factory.service";
import { DirectorySearchService } from "./services/directory-search.service";
import { DirectorySearchConfigurationModel } from "./models/directory-search-configuration.model";
import { DirectoryItemModel } from "./models/directory-item.model";
import { GoogleMapService } from "./components/google-map/google-map.service";

@Component({
    selector: "pcg-ng-directory-search",// DirectorySearchComponent.SELECTOR,
    templateUrl: "./directory-search.component.html"
})
export class DirectorySearchComponent implements OnInit, OnDestroy {
    public static SELECTOR: string = "pcg-ng-directory-search";

    @ViewChild("mapContainer", {static: false})
    public mapContainerRef: GoogleMapComponent;

    public paginationOptions: PaginationOptionsModel;
    public configuration: DirectorySearchConfigurationModel;
    protected pageModel$: Observable<DirectorySearchPageModel>;

    protected selectedSection: string;
    protected selectedAttributes: Array<string> = [];
    protected selectedLocations: Array<string> = [];
    protected selectedPageNumber: number;
    protected selectedSorting: string = "Name;asc";
    protected selectedSearchText: string = "";

    public searchFilters$: Observable<Array<SearchFilterMetadataModel>>;
    public searchParameters$: BehaviorSubject<SearchParametersModel>;
    public searchResult$: Observable<BaseSearchResultsModel<DirectoryItemModel>>;

    @Input() public activeSearchTab: string;
    @Output() protected searchTabChange = new EventEmitter<string>();

    public get isLocked$(): Observable<boolean> {
        return this._lockService.isLocked$;
    }

    public constructor(
        private _browserWindowService: BrowserWindowService,
        private _paginationOptionsFactory: PaginationOptionsFactoryService,
        private _searchParametersFactory: DirectorySearchParametersFactoryService,
        private _directorySearchService: DirectorySearchService,
        private _deliveryApiService: DeliveryApiService,
        private _lockService: LockService,
        public _googleMapService: GoogleMapService
    ) {
    }

    public ngOnInit(): void {
        this.pageModel$ = this._deliveryApiService.getPage$<DirectorySearchPageModel>(window.location.pathname);

        this.paginationOptions = this._paginationOptionsFactory.createDefaultPaginationOptions();
        this.paginationOptions.showGoToPrevious = true;
        this.paginationOptions.showGoToNext = true;

        const initialParameters = this._searchParametersFactory.createFromQueryString(this._browserWindowService.getQueryString());
        this.selectedSorting = initialParameters.sortOption;
        this.selectedSection = initialParameters.templateId ? initialParameters.templateId : undefined;
        this.selectedAttributes = initialParameters.attributeIds;

        this.selectedLocations = initialParameters.locationIds;
        this.selectedSearchText = initialParameters.searchText;
        initialParameters.searchTab = this.activeSearchTab;

        this.searchParameters$ = new BehaviorSubject<SearchParametersModel>(initialParameters);

        this.searchFilters$ = this.searchParameters$.pipe(
            map(parameters => parameters.templateId),
            distinctUntilChanged(),
            switchMap(templateId  => {
                const currentTemplateId = templateId || "00000000-0000-0000-0000-000000000000";
                return this._directorySearchService.getTemplateMetadata(currentTemplateId);
            })
        );

        this.searchResult$ = this.searchParameters$.pipe(
            tap(() => this._lockService.lock()),
            tap(() => this._googleMapService.clear()),
            tap(parameters => this.setQueryString(parameters)),
            map(parameters => this.mapParameters(parameters)),
            mergeMap(parameters => this._directorySearchService.getSearchResults(parameters)),
            tap(() => this._lockService.unlock()),
            share()
        );
    }

    public ngOnDestroy(): void {
    }

    protected getFilter(alias: string, filters: Array<SearchFilterMetadataModel>): SearchFilterMetadataModel {
        return filters.find(f => f.alias === alias);
    }

    protected setTemplateId(templateId: string): void {
        this.selectedAttributes = [];
        const currentParameters = this.searchParameters$.value;
        currentParameters.templateId = templateId ? templateId : "";
        currentParameters.attributeIds = [];
        currentParameters.pageNumber = 1;
        this.searchParameters$.next(currentParameters);
    }

    public setAttributes(attributeIds: Array<string>): void {
        this.selectedAttributes = attributeIds;
        const currentParameters = {...this.searchParameters$.value};
        currentParameters.attributeIds = attributeIds;
        currentParameters.pageNumber = 1;
        this.searchParameters$.next(currentParameters);
    }

    public setLocations(locationsIds: Array<string>): void {
        this.selectedLocations = locationsIds;
        const currentParameters = {...this.searchParameters$.value};
        currentParameters.locationIds = locationsIds;
        currentParameters.pageNumber = 1;
        this.searchParameters$.next(currentParameters);
    }

    private mapParameters(parameters: SearchParametersModel): SearchParametersModel {
        const mapped = {...parameters};
        mapped.categories = [...(parameters.attributeIds ?? []), ...(parameters.locationIds ?? [])];
        return mapped;
    }

    private setQueryString(parameters: SearchParametersModel): void {
        this._browserWindowService.setQueryString(this._searchParametersFactory.serialiseToQueryString(parameters));
    }

    protected setPageNumber(pageNumber: number): void {
        this.selectedPageNumber = pageNumber;
        const parameters = {...this.searchParameters$.value};
        parameters.pageNumber = pageNumber ?? 1;
        this.searchParameters$.next(parameters);
    }

    protected setSorting(sorting: string): void {
        this.selectedSorting = sorting;
        const parameters = {...this.searchParameters$.value};
        parameters.sortOption = sorting;
        parameters.pageNumber = 1;
        this.searchParameters$.next(parameters);
    }

    protected setSearchText(): void {
        const parameters = { ...this.searchParameters$.value };
        parameters.searchText = this.selectedSearchText?.trim() || "";
        parameters.pageNumber = 1;
        parameters.sortOption = "Rank;asc";
        this.selectedSorting = parameters.sortOption;
        this.searchParameters$.next(parameters);
    }

    public handleSearchTabChange(tab: string): void {
        this.searchTabChange.emit(tab);
    }
}
